import { useState, useEffect, memo } from "react";
import { useLang } from "@/i18n/LangContext";

interface SteamSale {
  name: string;
  key: string;
  start: string;
  end: string;
}

interface SalePattern {
  key: string;
  name: string;
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
}

const SALE_PATTERNS: SalePattern[] = [
  { key: "lunarNewYear", name: "Steam 설날 세일", startMonth: 1, startDay: 22, endMonth: 1, endDay: 29 },
  { key: "spring", name: "Steam 봄 세일", startMonth: 3, startDay: 12, endMonth: 3, endDay: 19 },
  { key: "summer", name: "Steam 여름 세일", startMonth: 6, startDay: 25, endMonth: 7, endDay: 9 },
  { key: "autumn", name: "Steam 가을 세일", startMonth: 11, startDay: 25, endMonth: 12, endDay: 1 },
  { key: "winter", name: "Steam 겨울 세일", startMonth: 12, startDay: 18, endMonth: 1, endDay: 2 },
];

function buildDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function computeSales(): SteamSale[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const candidates: SteamSale[] = [];

  for (const year of [currentYear - 1, currentYear, currentYear + 1]) {
    for (const pattern of SALE_PATTERNS) {
      const endYear = pattern.endMonth < pattern.startMonth ? year + 1 : year;
      candidates.push({
        name: pattern.name,
        key: pattern.key,
        start: buildDate(year, pattern.startMonth, pattern.startDay),
        end: buildDate(endYear, pattern.endMonth, pattern.endDay),
      });
    }
  }

  candidates.sort((a, b) => a.start.localeCompare(b.start));
  const nowISO = now.toISOString().slice(0, 10);
  return candidates.filter((sale) => sale.end >= nowISO);
}

const KNOWN_SALES: SteamSale[] = computeSales();

function getNextSale(): { sale: SteamSale; status: "upcoming" | "ongoing" } | null {
  const now = new Date();
  for (const sale of KNOWN_SALES) {
    const start = new Date(sale.start + "T10:00:00-08:00");
    const end = new Date(sale.end + "T10:00:00-08:00");
    if (now >= start && now < end) return { sale, status: "ongoing" };
  }
  for (const sale of KNOWN_SALES) {
    const start = new Date(sale.start + "T10:00:00-08:00");
    if (now < start) return { sale, status: "upcoming" };
  }
  return null;
}

export default memo(function SaleCountdown() {
  const { t } = useLang();
  const [timeStr, setTimeStr] = useState("");
  const [saleInfo, setSaleInfo] = useState<ReturnType<typeof getNextSale>>(null);

  useEffect(() => { setSaleInfo(getNextSale()); }, []);

  useEffect(() => {
    if (!saleInfo) return;
    const update = () => {
      const now = new Date();
      const target = saleInfo.status === "ongoing"
        ? new Date(saleInfo.sale.end + "T10:00:00-08:00")
        : new Date(saleInfo.sale.start + "T10:00:00-08:00");
      const diff = target.getTime() - now.getTime();
      if (diff <= 0) { setTimeStr(t.saleCountdown?.startingSoon || "곧 시작"); return; }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      if (days > 0) setTimeStr(`${days}${t.common?.day || "일"} ${hours}${t.common?.hour || "시간"} ${minutes}${t.common?.minute || "분"}`);
      else setTimeStr(`${hours}${t.common?.hour || "시간"} ${minutes}${t.common?.minute || "분"} ${seconds.toString().padStart(2, "0")}${t.common?.second || "초"}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [saleInfo, t]);

  if (!saleInfo || !timeStr) return null;

  const isOngoing = saleInfo.status === "ongoing";
  const saleName = (t.saleCountdown as Record<string, string>)?.[saleInfo.sale.key] || saleInfo.sale.name;

  return (
    <div className="rounded-2xl border shadow-sm p-4 sm:p-5 mb-6 bg-card-bg dark:bg-card-bg border-gray-200 dark:border-card-border">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-900">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {isOngoing ? (
              <><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>
            ) : (
              <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>
            )}
          </svg>
        </div>
        <h2 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{saleName}</h2>
        {isOngoing && (
          <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded animate-pulse">
            {t.saleCountdown?.live || "LIVE"}
          </span>
        )}
      </div>
      <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">
        {isOngoing ? (t.saleCountdown?.saleEndsIn || "세일 종료까지") : (t.saleCountdown?.saleStartsIn || "세일 시작까지")}{" "}
        <span className={`font-bold tabular-nums ${isOngoing ? "text-emerald-600 dark:text-emerald-400" : "text-orange-600 dark:text-orange-400"}`}>{timeStr}</span>
        <span className="text-gray-400 dark:text-sub-text ml-2">
          ({new Date(saleInfo.sale.start).toLocaleDateString("ko-KR", { month: "short", day: "numeric" })}
          {" ~ "}
          {new Date(saleInfo.sale.end).toLocaleDateString("ko-KR", { month: "short", day: "numeric" })})
        </span>
      </p>
    </div>
  );
});
